import axiosInstance, { fetcher, revalidateOnlyStaleOptions } from 'src/utils/axios';
import useSWR from 'swr';
import { useMemo } from 'react';
import { getURL, cleanParams } from 'src/utils/url';
import { ENDPOINTS } from './endpoints';
import { isEmpty } from 'src/utils/type_check';

export function useGetStockList(params) {
  const URL = getURL(ENDPOINTS.reference.get_stock_list, params);

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, revalidateOnlyStaleOptions);

  const memoizedValue = useMemo(
    () => ({
      searchResults: data?.result?.items || [],
      totalCount: data?.result?.totalCount || 0,
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, error, isLoading, isValidating],
  );

  return memoizedValue;
}

export function useGetDateTypes(target) {
  const URL = target ? getURL(ENDPOINTS.reference.get_date_type, { target }) : null;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, revalidateOnlyStaleOptions);

  const memoizedValue = useMemo(
    () => ({
      options: (data?.result?.items || []).map((item) => ({
        label: item.dateTypeName,
        value: item.dateType,
      })),
      defaultDateType: data?.result?.defaultDateType || '',
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, error, isLoading, isValidating],
  );

  return memoizedValue;
}

export function useGetProductCodeTypes() {
  const { data, isLoading, error, isValidating } = useSWR(
    ENDPOINTS.reference.get_product_code_type,
    fetcher,
    revalidateOnlyStaleOptions,
  );

  const memoizedValue = useMemo(
    () => ({
      searchResults: [...(data?.result || [])].sort((a, b) => a.displayOrder - b.displayOrder),
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, error, isLoading, isValidating],
  );
  return memoizedValue;
}

export function useGetStockType() {
  const { data, isLoading, error, isValidating } = useSWR(
    ENDPOINTS.reference.get_stock_type,
    fetcher,
    revalidateOnlyStaleOptions,
  );

  const memoizedValue = useMemo(
    () => ({
      searchResults: data?.result || [],
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, error, isLoading, isValidating],
  );
  return memoizedValue;
}

export function useGetFeatureSettingReference(params) {
  const URL = isEmpty(params?.featureKey)
    ? null
    : getURL(ENDPOINTS.reference.get_feature_setting, cleanParams(params));

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, revalidateOnlyStaleOptions);

  const memoizedValue = useMemo(
    () => ({
      searchResults: data?.result || {},
      searchPreview: data?.result?.preview || null,
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, error, isLoading, isValidating],
  );

  return memoizedValue;
}

export function useGetCourierList(id) {
  const URL = id ? getURL(ENDPOINTS.reference.get_courier_list(id)) : null;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, revalidateOnlyStaleOptions);

  const memoizedValue = useMemo(
    () => ({
      searchResults: data?.result || {},
      searchAccounts: data?.result?.accounts || [],
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, error, isLoading, isValidating],
  );

  return memoizedValue;
}

export function useGetBulkCourierList(id) {
  const URL = id ? getURL(ENDPOINTS.reference.get_bulk_courier_list(id)) : null;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, revalidateOnlyStaleOptions);

  const memoizedValue = useMemo(
    () => ({
      searchResults: data?.result || {},
      searchAccounts: data?.result?.accounts || [],
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, error, isLoading, isValidating],
  );

  return memoizedValue;
}

export async function getCartAvailability(locationId, cartBarcode) {
  const ret = await axiosInstance.get(
    ENDPOINTS.reference.get_cart_availability(locationId, cartBarcode),
  );
  return ret.data;
}

export function useGetShipperCourierReference(params) {
  const URL = isEmpty(params?.userId)
    ? null
    : getURL(ENDPOINTS.reference.get_shipper_courier, cleanParams(params));

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, revalidateOnlyStaleOptions);

  const memoizedValue = useMemo(
    () => ({
      searchResults: data?.result || [],
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, error, isLoading, isValidating],
  );

  return memoizedValue;
}

export function useGetReferenceChannels(params) {
  const { search, ...query } = params ?? {};
  const URL = isEmpty(query.UserId) ? null : getURL(ENDPOINTS.reference.get_channel, query);
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, revalidateOnlyStaleOptions);

  const memoizedValue = useMemo(() => {
    const list = Array.isArray(data?.result) ? data.result : data?.result?.list || [];
    const keyword = (search ?? '').trim().toLowerCase();
    return {
      searchResults: keyword
        ? list.filter((c) => (c.channelName ?? '').toLowerCase().includes(keyword))
        : list,
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    };
  }, [data?.result, search, error, isLoading, isValidating]);
  return memoizedValue;
}

const CUSTOMER_SEARCH_FIELDS = [
  'userFirstNameLocal',
  'userFirstNameEnglish',
  'userNameLocal',
  'userNameEnglish',
];

export function useGetReferenceCustomers(params) {
  const { search, locationId } = params ?? {};
  const URL = isEmpty(locationId) ? null : getURL(ENDPOINTS.reference.get_customer, { locationId });
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, revalidateOnlyStaleOptions);

  const memoizedValue = useMemo(() => {
    const list = Array.isArray(data?.result) ? data.result : [];
    const keyword = (search ?? '').trim().toLowerCase();
    return {
      searchResults: keyword
        ? list.filter((c) =>
            CUSTOMER_SEARCH_FIELDS.some((f) => (c[f] ?? '').toLowerCase().includes(keyword)),
          )
        : list,
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    };
  }, [data?.result, search, error, isLoading, isValidating]);
  return memoizedValue;
}

export function useGetReferenceChannelMappingCodes(params) {
  const { isActive, ...query } = params ?? {};
  const { searchResults: channels, ...rest } = useGetReferenceChannels(query);
  const memoizedValue = useMemo(
    () => ({
      ...rest,
      searchResults: channels
        .filter((channel) => !isActive || channel.isActive)
        .flatMap((channel) =>
          (channel.mappingCodes ?? []).map((mappingCode) => ({
            ...channel,
            mappingCode,
            hasMultipleCodes: (channel.mappingCodes ?? []).length > 1,
          })),
        ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channels, isActive, rest.searchLoading, rest.searchError, rest.searchValidating],
  );
  return memoizedValue;
}
